import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useGame } from '../../context/GameContext';
import { ALL_EQUIPMENT, ALL_ITEMS, DUNGEON_RESOURCES, isLootbox } from '../../data/dungeon';
import { getDefaultPoolsForTier } from '../../data/world-map/dungeonBiomes';
import ItemChip from '../UI/ItemChip';
import ItemInfoModal from '../UI/ItemInfoModal';
import ItemContextMenu from '../UI/ItemContextMenu';
import StatChip from '../UI/StatChip';
import CharacterGuideModal from '../modals/CharacterGuideModal';
import StarForceModal from '../modals/StarForceModal';
import ForgingModal from '../modals/ForgingModal';
import ChestOpenModal from '../modals/ChestOpenModal';
import './PlayerPanel.css';

/**
 * Memoized Inventory Slot Component
 * Only re-renders when its specific item or state changes, not when other slots change.
 */
const InventorySlot = memo(({
  item,
  displayIndex,
  isSelected,
  isDragging,
  isDragOver,
  isFocused,
  deleteMode,
  contextMenu,
  equipment,
  playerStats,
  onContextMenu,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onClick,
  onDoubleClick,
}) => {
  if (!item) {
    return (
      <div
        className={`inventory-slot empty-inv-slot ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      />
    );
  }

  return (
    <div
      className={`inventory-slot filled-inv-slot ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${isFocused ? 'focused' : ''}`}
      data-rarity={item.rarity}
      onContextMenu={onContextMenu}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div
        draggable={!deleteMode}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: deleteMode ? 'pointer' : 'grab' }}
      >
        <ItemChip
          item={item}
          amount={item.quantity || null}
          size="medium"
          showTooltip={!contextMenu}
          className="inventory-item-chip"
          equippedItem={item.slot ? equipment[item.slot] : null}
          playerStats={playerStats}
        />
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render if these change
  return (
    prevProps.item?.instanceId === nextProps.item?.instanceId &&
    prevProps.item?.quantity === nextProps.item?.quantity &&
    prevProps.item?.starForce === nextProps.item?.starForce &&
    prevProps.displayIndex === nextProps.displayIndex &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.isDragOver === nextProps.isDragOver &&
    prevProps.isFocused === nextProps.isFocused &&
    prevProps.deleteMode === nextProps.deleteMode &&
    prevProps.contextMenu === nextProps.contextMenu
  );
});

InventorySlot.displayName = 'InventorySlot';

/**
 * Player Panel - Character Sheet (MapleStory Style)
 *
 * Shows:
 * - Expanded equipment slots (12 slots total)
 * - 5x10 inventory grid (50 slots)
 * - Inventory management (sort, delete, equip/use items)
 */
const PlayerPanel = () => {
  const {
    playerCharacter,
    getPlayerStats,
    equipItem,
    unequipItem,
    enterDungeon,
    addToInventory,
    removeFromInventoryByIndex,
    removeFromInventoryByInstanceId,
    moveInventoryItem,
    swapInventoryItems,
    stackInventoryItems,
    setItemGridPosition,
    autoSortInventory,
    allocateStatPoint,
    addExperience, // For testing
    dungeonInventory, // For displaying materials in Misc tab
    useConsumable,
    getActiveBuffs,
    useCleanSlateScroll,
    setPlayerName,
    setPlayerTitle,
  } = useGame();

  const stats = getPlayerStats();
  const equipment = playerCharacter.equipment;
  const inventory = playerCharacter.inventory;
  const bonusStats = playerCharacter.bonusStats || {};

  // Show character guide on first visit
  const [showCharacterGuide, setShowCharacterGuide] = useState(false);

  // Name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(playerCharacter.name || '');
  const nameInputRef = React.useRef(null);

  // Track which combat stats to highlight (green pulse) after stat allocation
  const [highlightedStats, setHighlightedStats] = useState(new Set());
  const highlightTimeoutRef = React.useRef(null);

  // Timer to update buff display (every second)
  const [, setBuffTick] = useState(0);
  const activeBuffs = getActiveBuffs();
  const hasActiveBuffs = Object.keys(activeBuffs).length > 0;

  // Level up celebration animation
  const [isLevelingUp, setIsLevelingUp] = useState(false);
  const prevLevelRef = React.useRef(playerCharacter.level || 1);

  // Notification toast for equipment errors
  const [notification, setNotification] = useState(null);
  const notificationTimeoutRef = React.useRef(null);

  // Chest opening modal state
  const [chestToOpen, setChestToOpen] = useState(null);

  // Show notification toast
  const showNotification = (message, type = 'error') => {
    // Clear existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    setNotification({ message, type });
    // Auto-hide after 3 seconds
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  // Detect level up and trigger celebration
  useEffect(() => {
    const currentLevel = playerCharacter.level || 1;
    if (currentLevel > prevLevelRef.current) {
      // Level increased - trigger celebration
      setIsLevelingUp(true);
      // Clear animation after 2 seconds
      const timer = setTimeout(() => {
        setIsLevelingUp(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    prevLevelRef.current = currentLevel;
  }, [playerCharacter.level]);

  useEffect(() => {
    // Only run timer if there are active buffs
    if (!hasActiveBuffs) return;

    const interval = setInterval(() => {
      setBuffTick(n => n + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [hasActiveBuffs]);

  // Character guide is now handled by the tutorial system (characterTab tutorial)
  // The ❓ button allows manual reopening of the guide


  // Format remaining buff time (mm:ss)
  const formatBuffTime = (expiresAt) => {
    const now = Date.now();
    const remaining = Math.max(0, expiresAt - now);
    const seconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Map primary stats to affected combat stats
  const getAffectedCombatStats = (primaryStat) => {
    const statMap = {
      STR: ['health', 'attack', 'defense'],
      DEX: ['attack', 'accuracy', 'critChance', 'evasion', 'attackSpeed'],
      INT: ['health', 'defense', 'accuracy', 'goldFind', 'xpGain'],
      LUK: ['critChance', 'critDamage', 'evasion', 'goldFind']
    };
    return statMap[primaryStat] || [];
  };

  // Handle stat allocation with green pulse effect
  const handleStatAllocation = (primaryStat) => {
    allocateStatPoint(primaryStat);

    // Get affected combat stats
    const affected = getAffectedCombatStats(primaryStat);

    // Keep highlight active - don't clear during spam clicking
    setHighlightedStats(new Set(affected));

    // Clear existing timeout if spam-clicking
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    // Set new timeout to remove highlight after 1.5s of inactivity
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedStats(new Set());
      highlightTimeoutRef.current = null;
    }, 1500);
  };

  // Calculate base stats (without equipment) and equipment bonuses
  const getBaseStatsAndBonuses = () => {
    const { STR, DEX, INT, LUK } = playerCharacter.primaryStats;
    const level = playerCharacter.level || 1;

    // Level-based stat bonuses (scales per level above 1)
    const levelBonus = level - 1;
    const levelStats = {
      health: levelBonus * 10,        // +10 HP per level
      attack: levelBonus * 2,         // +2 ATK per level
      defense: levelBonus * 1,        // +1 DEF per level
      critChance: levelBonus * 0.002, // +0.2% crit per level
      critDamage: levelBonus * 0.02,  // +2% crit damage per level
      accuracy: levelBonus * 0.005,   // +0.5% accuracy per level
      evasion: levelBonus * 0.002,    // +0.2% evasion per level
      attackSpeed: levelBonus * 0.01, // +0.01x attack speed per level
    };

    // Calculate equipment bonuses
    const equipStats = {
      health: 0,
      attack: 0,
      defense: 0,
      critChance: 0,
      critDamage: 0,
      speed: 0,
      range: 0,
      accuracy: 0,
      evasion: 0,
      attackSpeed: 0,
      goldFind: 0,
      experienceGain: 0,
    };

    Object.values(playerCharacter.equipment).forEach(item => {
      if (item) {
        // Add base equipment stats (already quality-scaled when item was generated)
        if (item.stats) {
          Object.entries(item.stats).forEach(([stat, value]) => {
            if (typeof equipStats[stat] === 'number') {
              equipStats[stat] += value;
            }
          });
        }

        // Add Star Force stat bonuses
        if (item.starForceStats) {
          Object.entries(item.starForceStats).forEach(([stat, value]) => {
            if (stat === 'allStats') {
              // allStats bonus is handled separately in final calculation
              // For display purposes, we don't add it to equipStats directly
            } else if (typeof equipStats[stat] === 'number') {
              equipStats[stat] += value;
            }
          });
        }
      }
    });

    // Calculate base stats (from primary stats + level bonuses, no equipment)
    const baseStats = {
      health: Math.floor(100 + (STR * 5) + (INT * 2) + levelStats.health),
      attack: Math.floor(10 + (STR * 1.5) + (DEX * 0.5) + levelStats.attack),
      defense: Math.floor(5 + (STR * 0.5) + (INT * 0.8) + levelStats.defense),
      critChance: 0.05 + (DEX * 0.003) + (LUK * 0.004) + levelStats.critChance,
      critDamage: 1.5 + (LUK * 0.01) + levelStats.critDamage,
      accuracy: 0.80 + (DEX * 0.005) + (INT * 0.003) + levelStats.accuracy,
      evasion: 0.05 + (DEX * 0.003) + (LUK * 0.002) + levelStats.evasion,
      attackSpeed: 1.0 + (DEX * 0.01) + levelStats.attackSpeed,
      speed: 3,
      range: 1,
      goldFind: (LUK * 0.005) + (INT * 0.003),
      experienceGain: 1.0 + (INT * 0.004),
    };

    // Calculate potential (cubing) bonuses from equipment
    const potentialStats = {
      STR: 0, DEX: 0, INT: 0, LUK: 0,
      attack: 0, defense: 0, health: 0,
      critChance: 0, critDamage: 0,
      allStats: 0, bossDamage: 0, ignoreDef: 0,
    };

    Object.values(playerCharacter.equipment).forEach(item => {
      if (item?.potentialLines) {
        item.potentialLines.forEach(line => {
          if (potentialStats.hasOwnProperty(line.stat)) {
            potentialStats[line.stat] += line.value;
          }
        });
      }
    });

    // Calculate buff bonuses
    // Buff values are interpreted as:
    // - Flat additive if value >= 1 (e.g., +100 HP, +2 SPD)
    // - Percentage additive for percentage-based stats if value < 1 (e.g., +0.15 = +15% crit)
    // - Percentage multiplier for other stats if value < 1 (e.g., +0.20 = +20% more attack)
    const now = Date.now();
    const currentActiveBuffs = playerCharacter.activeBuffs || {};
    const buffValues = {}; // Raw buff values

    // Stats that are already percentages (buff value adds directly as percentage points)
    const percentageStats = new Set([
      'critChance', 'critDamage', 'accuracy', 'evasion', 'attackSpeed'
    ]);

    Object.values(currentActiveBuffs).forEach(buff => {
      if (buff.expiresAt <= now) return; // Skip expired

      // Single stat buff
      if (buff.stat && buff.value) {
        buffValues[buff.stat] = (buffValues[buff.stat] || 0) + buff.value;
      }
      // Multi-stat buff
      if (buff.stats) {
        Object.entries(buff.stats).forEach(([stat, value]) => {
          buffValues[stat] = (buffValues[stat] || 0) + value;
        });
      }
    });

    // Calculate actual buff bonus values (the amount added by buffs)
    const buffStats = {};
    Object.keys(baseStats).forEach(stat => {
      const baseWithEquip = baseStats[stat] + (equipStats[stat] || 0);
      const buffValue = buffValues[stat] || 0;

      if (buffValue >= 1) {
        // Flat bonus: +100 HP, +2 SPD - shows directly
        buffStats[stat] = buffValue;
      } else if (percentageStats.has(stat)) {
        // Percentage stat: +0.15 adds 15% directly
        buffStats[stat] = buffValue;
      } else {
        // Multiplier: calculate the actual gain from the multiplier
        buffStats[stat] = baseWithEquip * buffValue;
      }
    });

    return { baseStats, equipStats, buffStats, potentialStats };
  };

  const { baseStats, equipStats, buffStats, potentialStats } = getBaseStatsAndBonuses();

  // Removed inventorySortBy state - now using one-time autoSortInventory button
  const [selectedItems, setSelectedItems] = useState(new Set()); // Set of actual inventory indices (unsorted)
  const [draggedItem, setDraggedItem] = useState(null); // { item, instanceId, sourceType: 'inventory'|'equipment', sourceSlot: number|string }
  const [dragOverSlot, setDragOverSlot] = useState(null); // Equipment slot being hovered
  const [dragTargetSlot, setDragTargetSlot] = useState(null); // Inventory grid slot being hovered (number)
  const [infoItem, setInfoItem] = useState(null); // Item to show info for
  const [activeTab, setActiveTab] = useState('equipment'); // equipment, consumables, misc, backpack
  const [deleteMode, setDeleteMode] = useState(false); // Toggle delete mode
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // Delete confirmation modal
  const [focusedSlot, setFocusedSlot] = useState(null); // For focus retention after equipping
  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { item, position: { x, y } }
  // Star Force enhancement modal
  const [starForceItem, setStarForceItem] = useState(null); // Item to enhance
  // Forging (scroll) modal
  const [forgingItem, setForgingItem] = useState(null); // Item to forge
  // Trash confirmation modal
  const [trashConfirm, setTrashConfirm] = useState(null); // { item, instanceId } - item being trashed

  // Handle equipping from inventory
  const handleEquipFromInventory = (item, displayIndex) => {
    const result = equipItem(item);
    if (!result.success) {
      showNotification(result.error, 'error');
    } else {
      // Set focus on this slot for rapid equipping
      setFocusedSlot(displayIndex);
      // Clear focus after 2 seconds
      setTimeout(() => setFocusedSlot(null), 2000);
    }
  };

  // Handle using consumable items (potions, buffs, etc.)
  const handleUseItem = (item) => {
    // Get fresh item from inventory by instanceId (handles stale references from rapid double-clicks)
    const freshItem = inventory.find(i => i.instanceId === item.instanceId);
    if (!freshItem) {
      // Item no longer exists (was fully consumed)
      setContextMenu(null);
      return;
    }

    // Check if item is usable
    if (!freshItem.usable) {
      return;
    }

    // Check if item is a lootbox/chest - show modal instead of direct use
    if (freshItem.category === 'lootbox' || isLootbox(freshItem.id)) {
      setChestToOpen(freshItem);
      setContextMenu(null); // Close menu for lootbox modal
      return;
    }

    // Check if this is the last item in stack - close menu after use
    const isLastItem = (freshItem.quantity || 1) <= 1;

    // Use the consumable via GameContext
    const result = useConsumable(freshItem);

    if (result.success) {
      console.log(`✅ Used ${freshItem.name}: ${result.message}`);
      // Close context menu if item is fully consumed
      if (isLastItem) {
        setContextMenu(null);
      }
    } else {
      console.warn(`❌ Failed to use ${freshItem.name}: ${result.error}`);
    }
  };

  // Context menu handlers
  const handleContextMenu = (item, e, isEquipped = false) => {
    e.preventDefault();
    setContextMenu({
      item,
      position: { x: e.clientX, y: e.clientY },
      isEquipped
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleDeleteFromContextMenu = (item) => {
    if (window.confirm(`Delete ${item.name}?`)) {
      removeFromInventoryByInstanceId(item.instanceId);
    }
  };

  const handleEnhanceFromContextMenu = (item) => {
    setStarForceItem(item);
  };

  const handleForgeFromContextMenu = (item) => {
    setForgingItem(item);
  };

  // Handle Clean Slate scroll usage from context menu
  const handleCleanSlateFromContextMenu = (item) => {
    const result = useCleanSlateScroll(item.instanceId);
    if (result.success) {
      // Could add a toast/notification here
      console.log(result.message);
    } else {
      console.error(result.error);
    }
  };

  // Handle collecting loot from chest modal
  const handleChestCollect = useCallback((loot) => {
    if (!chestToOpen || !loot) return;

    // Add all items to inventory
    loot.forEach(drop => {
      if (drop.type === 'equipment') {
        addToInventory(drop.item);
      } else if (drop.type === 'material' || drop.type === 'consumable') {
        const itemToAdd = {
          ...drop.item,
          instanceId: `${drop.item.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
          quantity: drop.quantity,
        };
        addToInventory(itemToAdd, drop.quantity);
      }
    });

    // Remove the chest from inventory
    removeFromInventoryByInstanceId(chestToOpen.instanceId);

    console.log(`✅ Opened ${chestToOpen.name}, collected ${loot.length} items`);
  }, [chestToOpen, addToInventory, removeFromInventoryByInstanceId]);

  // Check if player has a Clean Slate Scroll in inventory
  const hasCleanSlateScroll = inventory.some(
    i => i.id === 'cleanSlateScroll' && (i.quantity || 1) > 0
  );

  const handleUnequipFromContextMenu = (item) => {
    // Find which slot this item is in
    const slot = Object.entries(equipment).find(([, eq]) => eq?.instanceId === item.instanceId)?.[0];
    if (slot) {
      unequipItem(slot);
    }
  };

  // Drag handlers (now uses instanceId instead of originalIndex)
  const handleDragStart = (item, instanceId, displayIndex, e) => {
    // Set data for drag operation (required for drag to work)
    e.dataTransfer.setData('text/plain', instanceId);
    e.dataTransfer.effectAllowed = 'move';

    // Create custom drag image from the ItemChip inside the slot
    const slot = e.currentTarget.closest('.inventory-slot');
    const itemChip = slot?.querySelector('.item-chip');
    if (itemChip) {
      // Clone the item chip for the drag image
      const clone = itemChip.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.opacity = '1';
      clone.style.transform = 'none';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);

      // Set as drag image, centered on cursor
      const rect = itemChip.getBoundingClientRect();
      e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2);

      // Remove clone after drag starts
      setTimeout(() => {
        document.body.removeChild(clone);
      }, 0);
    }

    setDraggedItem({
      item,
      instanceId,
      sourceType: 'inventory',
      sourceSlot: displayIndex
    });
  };

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverSlot(null);
    setDragTargetSlot(null);
  }, []);

  // Handle drag start from equipment slot
  const handleDragStartFromEquipment = (equipment, slot, e) => {
    // Set data for drag operation (required for drag to work)
    e.dataTransfer.setData('text/plain', `equipment_${slot}`);
    e.dataTransfer.effectAllowed = 'move';

    // Create custom drag image from the ItemChip inside the equipment slot
    const eqSlot = e.currentTarget.closest('.eq-slot-filled');
    const itemChip = eqSlot?.querySelector('.item-chip');
    if (itemChip) {
      // Clone the item chip for the drag image
      const clone = itemChip.cloneNode(true);
      clone.style.position = 'absolute';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.opacity = '1';
      clone.style.transform = 'none';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);

      // Set as drag image, centered on cursor
      const rect = itemChip.getBoundingClientRect();
      e.dataTransfer.setDragImage(clone, rect.width / 2, rect.height / 2);

      // Remove clone after drag starts
      setTimeout(() => {
        document.body.removeChild(clone);
      }, 0);
    }

    setDraggedItem({
      item: equipment,
      instanceId: `equipment_${slot}`,
      sourceType: 'equipment',
      sourceSlot: slot
    });
  };

  const handleDragOverSlot = (slot, e) => {
    e.preventDefault();
    if (draggedItem && draggedItem.item.slot) {
      // Check if item can go in this slot
      const itemSlot = draggedItem.item.slot;
      // Handle ring and pendant slots (ring1/ring2 can accept 'ring', pendant1 can accept 'pendant')
      const canDrop =
        itemSlot === slot ||
        (slot.startsWith('ring') && itemSlot === 'ring') ||
        (slot.startsWith('pendant') && itemSlot === 'pendant');

      if (canDrop) {
        setDragOverSlot(slot);
      }
    }
  };

  const handleDragLeaveSlot = () => {
    setDragOverSlot(null);
  };

  const handleDropOnSlot = (slot, e) => {
    e.preventDefault();
    if (draggedItem) {
      const result = equipItem(draggedItem.item);
      if (!result.success) {
        console.error('Failed to equip item:', result.error);
        showNotification(result.error || 'Could not equip item', 'error');
      } else {
        console.log('Successfully equipped:', draggedItem.item.name, 'in slot:', draggedItem.item.slot);
        console.log('Current equipment:', playerCharacter.equipment);
      }
    }
    setDraggedItem(null);
    setDragOverSlot(null);
  };

  const handleDropOnTrash = (e) => {
    e.preventDefault();
    if (draggedItem) {
      // Prevent deletion of dungeon materials
      if (draggedItem.item.isDungeonMaterial) {
        showNotification('Dungeon materials cannot be deleted from inventory.', 'error');
        setDraggedItem(null);
        return;
      }

      // Show trash confirmation modal
      setTrashConfirm({
        item: draggedItem.item,
        instanceId: draggedItem.instanceId
      });
    }
    setDraggedItem(null);
  };

  // Confirm trash deletion
  const confirmTrashDelete = () => {
    if (trashConfirm) {
      removeFromInventoryByInstanceId(trashConfirm.instanceId);
      showNotification(`Deleted ${trashConfirm.item.name}`, 'success');
    }
    setTrashConfirm(null);
  };

  // Cancel trash deletion
  const cancelTrashDelete = () => {
    setTrashConfirm(null);
  };

  // Inventory grid drag handlers (memoized to prevent unnecessary re-renders)
  const handleDragOverInventorySlot = useCallback((targetIndex, e) => {
    e.preventDefault();
    setDragTargetSlot(targetIndex);
  }, []);

  const handleDragLeaveInventorySlot = useCallback(() => {
    setDragTargetSlot(null);
  }, []);

  const handleDropOnInventorySlot = (targetIndex, targetItem, e) => {
    e.preventDefault();

    if (!draggedItem) return;

    // Don't drop on self
    if (draggedItem.sourceType === 'inventory' && draggedItem.sourceSlot === targetIndex) {
      setDraggedItem(null);
      setDragTargetSlot(null);
      return;
    }

    // Case 1: Dropping from equipment to inventory
    if (draggedItem.sourceType === 'equipment') {
      // Unequip the item (it will be added to inventory automatically)
      const result = unequipItem(draggedItem.sourceSlot);
      if (!result.success) {
        alert(result.error);
      }
      setDraggedItem(null);
      setDragTargetSlot(null);
      return;
    }

    // Case 2: Dropping from inventory to inventory
    if (draggedItem.sourceType === 'inventory') {
      const sourceItem = draggedItem.item;
      const sourceInstanceId = draggedItem.instanceId;

      // Find actual inventory array indices
      const sourceActualIndex = inventory.findIndex(item => item.instanceId === sourceInstanceId);

      if (sourceActualIndex === -1) {
        console.error('Source item not found in inventory');
        setDraggedItem(null);
        setDragTargetSlot(null);
        return;
      }

      // If target is empty, set the item's grid position
      if (!targetItem) {
        setItemGridPosition(sourceInstanceId, targetIndex);
        setDraggedItem(null);
        setDragTargetSlot(null);
        return;
      }

      // If target has an item, find its actual index
      const targetInstanceId = targetItem.instanceId;
      const targetActualIndex = inventory.findIndex(item => item.instanceId === targetInstanceId);

      if (targetActualIndex === -1) {
        console.error('Target item not found in inventory');
        setDraggedItem(null);
        setDragTargetSlot(null);
        return;
      }

      // Check if we can stack
      const canStack = (
        sourceItem.id === targetItem.id &&
        sourceItem.category === targetItem.category &&
        (sourceItem.stackSize && sourceItem.stackSize > 1) &&
        (targetItem.quantity || 1) < (targetItem.stackSize || 99)
      );

      if (canStack) {
        // Stack items together
        stackInventoryItems(sourceActualIndex, targetActualIndex);
      }
      // If can't stack and slot is occupied, do nothing (no swapping)
    }

    setDraggedItem(null);
    setDragTargetSlot(null);
  };

  // Handle unequipping
  const handleUnequip = (slot) => {
    const result = unequipItem(slot);
    if (!result.success) {
      alert(result.error);
    }
  };

  // Add test items (for development/testing)
  const addTestItems = () => {
    const testItems = [
      // Equipment - Weapon
      ALL_EQUIPMENT.rustyDagger,

      // Equipment - Offhand
      ALL_EQUIPMENT.woodenShield,

      // Equipment - Helmet
      ALL_EQUIPMENT.leatherCap,

      // Equipment - Cape
      ALL_EQUIPMENT.woolCape,

      // Equipment - Top
      ALL_EQUIPMENT.leatherVest,

      // Equipment - Bottom
      ALL_EQUIPMENT.clothPants,

      // Equipment - Gloves
      ALL_EQUIPMENT.leatherGloves,

      // Equipment - Boots
      ALL_EQUIPMENT.leatherBoots,

      // Equipment - Belt
      ALL_EQUIPMENT.leatherBelt,

      // Equipment - Pendant
      ALL_EQUIPMENT.copperAmulet,

      // Equipment - Ring (2 items for 2 slots)
      ALL_EQUIPMENT.copperRing,
      ALL_EQUIPMENT.silverRing,

      // Consumables - Health Potions
      ALL_ITEMS.minorHealthPotion,
      ALL_ITEMS.healthPotion,
      ALL_ITEMS.greaterHealthPotion,

      // Consumables - Buff Potions
      ALL_ITEMS.strengthPotion,
      ALL_ITEMS.defensePotion,
      ALL_ITEMS.swiftnessPotion,

      // Consumables - Food
      ALL_ITEMS.bread,
      ALL_ITEMS.cookedMeat,

      // Buff Items
      ALL_ITEMS.scrollOfFortitude,
      ALL_ITEMS.scrollOfPrecision,
      ALL_ITEMS.luckyCharm,

      // Misc Items
      ALL_ITEMS.teleportScroll,
      ALL_ITEMS.treasureMap,
    ];

    console.log('🧪 Test items to add:', testItems.length);
    console.log('📦 Sample items:', testItems.slice(0, 5));
    testItems.forEach(item => {
      console.log('Adding item:', item?.name || 'undefined', item);
      addToInventory(item);
    });
    alert(`Added ${testItems.length} test items to inventory! (Equipment + Potions + Buffs + Misc)`);
  };

  // Show delete confirmation modal
  const requestDeleteItems = () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirm(true);
  };

  // Confirm and delete selected items
  const confirmDeleteItems = () => {
    // Filter out dungeon materials from selection
    const activeItems = categories[activeTab];
    const instanceIds = Array.from(selectedItems).filter(instanceId => {
      const item = activeItems.find(i => i.instanceId === instanceId);
      return item && !item.isDungeonMaterial;
    });

    // Delete each selected item by instanceId (excluding dungeon materials)
    instanceIds.forEach(instanceId => {
      removeFromInventoryByInstanceId(instanceId);
    });

    setSelectedItems(new Set());
    setDeleteMode(false); // Exit delete mode after deletion
    setShowDeleteConfirm(false);

    // NOTE: Auto-fill from backpack happens automatically!
    // The categorizeInventory() function runs on every render and automatically
    // places items in the correct tab based on the 50-item limit.
    // When items are deleted, the next render will recategorize and items from
    // backpack will naturally fill the empty slots in their respective tabs.
  };

  // Cancel delete - reset delete mode and clear selections
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedItems(new Set());
    setDeleteMode(false);
  };

  // Categorize inventory items
  const categorizeInventory = () => {
    const categories = {
      equipment: [],
      consumables: [],
      misc: [],
      backpack: []
    };

    const MAX_PER_CATEGORY = 50;

    // Items are already sorted by autoSortInventory when user clicks the button
    // Just categorize them based on their properties
    inventory.forEach((item) => {
      const itemWithInstanceId = { ...item };

      // Equipment items (has slot property)
      if (item.slot) {
        if (categories.equipment.length < MAX_PER_CATEGORY) {
          categories.equipment.push(itemWithInstanceId);
        } else {
          categories.backpack.push(itemWithInstanceId);
        }
      }
      // Consumables (potions, food, buff items)
      else if (item.category === 'potion' || item.category === 'buff') {
        if (categories.consumables.length < MAX_PER_CATEGORY) {
          categories.consumables.push(itemWithInstanceId);
        } else {
          categories.backpack.push(itemWithInstanceId);
        }
      }
      // Everything else (materials, quest items, misc)
      else {
        if (categories.misc.length < MAX_PER_CATEGORY) {
          categories.misc.push(itemWithInstanceId);
        } else {
          categories.backpack.push(itemWithInstanceId);
        }
      }
    });

    // Add dungeon materials from dungeonInventory to Misc tab
    if (dungeonInventory && DUNGEON_RESOURCES) {
      Object.entries(dungeonInventory).forEach(([materialId, quantity]) => {
        if (quantity > 0) {
          const material = DUNGEON_RESOURCES[materialId];
          if (material) {
            // Create a virtual item for display (with special flag)
            const virtualMaterial = {
              ...material,
              instanceId: `dungeon_material_${materialId}`,
              quantity: quantity,
              isDungeonMaterial: true, // Flag to prevent deletion
            };

            if (categories.misc.length < MAX_PER_CATEGORY) {
              categories.misc.push(virtualMaterial);
            }
            // Don't add to backpack - materials stay in misc only
          }
        }
      });
    }

    return categories;
  };

  // Memoize categories to prevent recalculation on unrelated state changes
  const categories = useMemo(() => categorizeInventory(), [inventory, dungeonInventory]);

  // Toggle item selection (now uses instanceId instead of index)
  const toggleItemSelection = (instanceId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(instanceId)) {
        next.delete(instanceId);
      } else {
        next.add(instanceId);
      }
      return next;
    });
  };

  return (
    <div className="player-panel">
      <div className="player-panel-header">
        <h2>⚔️ Character Sheet</h2>
      </div>

      <div className="player-panel-content">
        {/* Left: Equipment Slots (12 slots) */}
        <div
          className="equipment-section-expanded"
          onDragOver={(e) => {
            e.preventDefault();
            // Accept any dragged item with a slot
            if (draggedItem && draggedItem.item.slot) {
              e.dataTransfer.dropEffect = 'move';
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Auto-equip the item to its correct slot
            if (draggedItem) {
              const result = equipItem(draggedItem.item);
              if (!result.success) {
                console.error('Failed to equip item:', result.error);
                showNotification(result.error || 'Could not equip item', 'error');
              } else {
                console.log('Successfully equipped:', draggedItem.item.name, 'in slot:', draggedItem.item.slot);
              }
            }
            setDraggedItem(null);
            setDragOverSlot(null);
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Equipment</h3>
            <button
              className="guide-help-btn"
              onClick={() => setShowCharacterGuide(true)}
              title="Character Guide"
            >
              ❓
            </button>
          </div>

          <div className="equipment-grid-expanded">
            {/* Row 1: Weapon & Offhand */}
            <EquipmentSlot slot="weapon" label="Weapon" icon="⚔️" equipment={equipment.weapon} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="offhand" label="Offhand" icon="🛡️" equipment={equipment.offhand} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />

            {/* Row 2: Helmet & Cape */}
            <EquipmentSlot slot="helmet" label="Helmet" icon="⛑️" equipment={equipment.helmet} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="cape" label="Cape" icon="🦸" equipment={equipment.cape} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />

            {/* Row 3: Top & Bottom */}
            <EquipmentSlot slot="top" label="Top" icon="👕" equipment={equipment.top} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="bottom" label="Bottom" icon="👖" equipment={equipment.bottom} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />

            {/* Row 4: Gloves & Boots */}
            <EquipmentSlot slot="gloves" label="Gloves" icon="🧤" equipment={equipment.gloves} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="boots" label="Boots" icon="👢" equipment={equipment.boots} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />

            {/* Row 5: Belt & Pendant */}
            <EquipmentSlot slot="belt" label="Belt" icon="⚙️" equipment={equipment.belt} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="pendant1" label="Pendant" icon="💎" equipment={equipment.pendant1} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />

            {/* Row 6: Ring 1 & Ring 2 */}
            <EquipmentSlot slot="ring1" label="Ring" icon="💍" equipment={equipment.ring1} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
            <EquipmentSlot slot="ring2" label="Ring" icon="💍" equipment={equipment.ring2} onUnequip={handleUnequip} onDragOver={handleDragOverSlot} onDragLeave={handleDragLeaveSlot} onDrop={handleDropOnSlot} dragOverSlot={dragOverSlot} onContextMenu={handleContextMenu} showTooltip={!contextMenu} />
          </div>
        </div>

        {/* Center: Character Display & Stats */}
        <div className="character-section">
          {/* Character Display */}
          <div className="character-display">
            <div className={`character-portrait ${isLevelingUp ? 'level-up-active' : ''}`}>
              {isLevelingUp && <span className="level-up-text">LEVEL UP!</span>}
              <span className="character-icon">👑</span>
              <div
                className={`character-name ${isEditingName ? 'editing' : 'clickable'}`}
                onClick={() => {
                  if (!isEditingName) {
                    setNameInput(playerCharacter.name || '');
                    setIsEditingName(true);
                    setTimeout(() => nameInputRef.current?.focus(), 0);
                  }
                }}
                title={isEditingName ? '' : 'Click to set name'}
              >
                {isEditingName ? (
                  <div className="name-edit-container">
                    <div className="title-toggle">
                      <button
                        className={`title-btn ${(playerCharacter.title || 'Emperor') === 'Emperor' ? 'active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => { e.stopPropagation(); setPlayerTitle('Emperor'); }}
                      >
                        Emperor
                      </button>
                      <button
                        className={`title-btn ${playerCharacter.title === 'Empress' ? 'active' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => { e.stopPropagation(); setPlayerTitle('Empress'); }}
                      >
                        Empress
                      </button>
                    </div>
                    <input
                      ref={nameInputRef}
                      type="text"
                      className="name-input"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onBlur={() => {
                        setPlayerName(nameInput);
                        setIsEditingName(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setPlayerName(nameInput);
                          setIsEditingName(false);
                        } else if (e.key === 'Escape') {
                          setIsEditingName(false);
                        }
                      }}
                      maxLength={20}
                      placeholder="Enter name..."
                    />
                  </div>
                ) : (
                  <>
                    <span className="emperor-title">{(playerCharacter.title || 'Emperor').toUpperCase()}</span>
                    {playerCharacter.name ? (
                      <span className="player-name">{playerCharacter.name}</span>
                    ) : (
                      <span className="player-name placeholder">Click to set name</span>
                    )}
                  </>
                )}
              </div>
              <div className="character-level">Level {playerCharacter.level || 1}</div>
              {/* Active Buffs - Square Icons below hero info */}
              {hasActiveBuffs && (
                <div className="buff-icons-container">
                  {Object.entries(activeBuffs).map(([buffId, buff]) => (
                    <div key={buffId} className="buff-icon-square" title="">
                      <span className="buff-icon-emoji">{buff.icon}</span>
                      {buff.stacks > 1 && (
                        <span className="buff-stack-count">{buff.stacks}×</span>
                      )}
                      <span className="buff-timer-overlay">{formatBuffTime(buff.expiresAt)}</span>
                      {/* Hover Tooltip */}
                      <div className="buff-tooltip">
                        <div className="buff-tooltip-header">
                          <span className="buff-tooltip-icon">{buff.icon}</span>
                          <span className="buff-tooltip-name">{buff.name}</span>
                          {buff.stacks > 1 && <span className="buff-tooltip-stacks">×{buff.stacks}</span>}
                        </div>
                        <div className="buff-tooltip-effect">
                          {buff.stat && `+${Math.round(buff.value * 100)}% ${buff.stat.toUpperCase()}`}
                          {buff.stats && Object.entries(buff.stats).map(([stat, val]) => (
                            <div key={stat}>+{Math.round(val * 100)}% {stat.toUpperCase()}</div>
                          ))}
                        </div>
                        <div className="buff-tooltip-time">
                          {formatBuffTime(buff.expiresAt)} remaining
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Experience Bar */}
            <div className="exp-bar-container">
              <div className="exp-bar-label">
                <span className="exp-icon">⭐</span>
                <span className="exp-text">
                  {playerCharacter.experience} / {playerCharacter.experienceToNext} XP
                </span>
              </div>
              <div className="exp-bar">
                <div
                  className="exp-bar-fill"
                  style={{
                    width: `${(playerCharacter.experience / playerCharacter.experienceToNext) * 100}%`
                  }}
                />
              </div>
            </div>

            {/* Health Bar */}
            <div className="health-bar-container">
              <div className="health-bar-label">
                <span className="health-icon">❤️</span>
                <span className="health-text">
                  {playerCharacter.currentHealth || stats.health} / {stats.health}
                </span>
              </div>
              <div className="health-bar">
                <div
                  className="health-bar-fill"
                  style={{
                    width: `${((playerCharacter.currentHealth || stats.health) / stats.health) * 100}%`
                  }}
                />
              </div>
            </div>

            {/* Stats Section - Combined Primary + Combat */}
            <div className="stats-section-combined">
              {/* Primary Stats */}
              <div className="primary-stats-panel">
                <div className="primary-stats-header">
                  <h4>Primary Stats</h4>
                  {playerCharacter.statPoints > 0 && (
                    <span className="stat-points-badge">{playerCharacter.statPoints} Points</span>
                  )}
                </div>
                <div className="primary-stats-grid">
                  <StatChip
                    type="primary"
                    icon="💪"
                    label="Strength"
                    value={playerCharacter.primaryStats.STR}
                    base={playerCharacter.primaryStats.STR}
                    shrineBonus={bonusStats.STR || 0}
                    potentialBonus={potentialStats.STR + potentialStats.allStats}
                    total={Math.floor((playerCharacter.primaryStats.STR + (bonusStats.STR || 0)) * (1 + (potentialStats.STR + potentialStats.allStats) / 100))}
                    hasPoints={playerCharacter.statPoints > 0}
                    onAllocate={() => handleStatAllocation('STR')}
                    tooltip="Strength increases HP, Attack, and Defense.&#10;+5 HP, +1.5 ATK, +0.5 DEF per point"
                  />
                  <StatChip
                    type="primary"
                    icon="🎯"
                    label="Dexterity"
                    value={playerCharacter.primaryStats.DEX}
                    base={playerCharacter.primaryStats.DEX}
                    shrineBonus={bonusStats.DEX || 0}
                    potentialBonus={potentialStats.DEX + potentialStats.allStats}
                    total={Math.floor((playerCharacter.primaryStats.DEX + (bonusStats.DEX || 0)) * (1 + (potentialStats.DEX + potentialStats.allStats) / 100))}
                    hasPoints={playerCharacter.statPoints > 0}
                    onAllocate={() => handleStatAllocation('DEX')}
                    tooltip="Dexterity increases Accuracy, Attack Speed, Crit Chance, and Evasion.&#10;+0.5 ATK, +0.5% ACC, +0.3% CRIT, +0.3% EVA, +0.01x SPD per point"
                  />
                  <StatChip
                    type="primary"
                    icon="🧠"
                    label="Intelligence"
                    value={playerCharacter.primaryStats.INT}
                    base={playerCharacter.primaryStats.INT}
                    shrineBonus={bonusStats.INT || 0}
                    potentialBonus={potentialStats.INT + potentialStats.allStats}
                    total={Math.floor((playerCharacter.primaryStats.INT + (bonusStats.INT || 0)) * (1 + (potentialStats.INT + potentialStats.allStats) / 100))}
                    hasPoints={playerCharacter.statPoints > 0}
                    onAllocate={() => handleStatAllocation('INT')}
                    tooltip="Intelligence increases HP, Defense, Accuracy, Gold Find, and XP Gain.&#10;+2 HP, +0.8 DEF, +0.3% ACC, +0.3% GOLD, +0.4% XP per point"
                  />
                  <StatChip
                    type="primary"
                    icon="🍀"
                    label="Luck"
                    value={playerCharacter.primaryStats.LUK}
                    base={playerCharacter.primaryStats.LUK}
                    shrineBonus={bonusStats.LUK || 0}
                    potentialBonus={potentialStats.LUK + potentialStats.allStats}
                    total={Math.floor((playerCharacter.primaryStats.LUK + (bonusStats.LUK || 0)) * (1 + (potentialStats.LUK + potentialStats.allStats) / 100))}
                    hasPoints={playerCharacter.statPoints > 0}
                    onAllocate={() => handleStatAllocation('LUK')}
                    tooltip="Luck increases Critical Hit Chance, Critical Damage, Evasion, and Gold Find.&#10;+0.4% CRIT, +1% CRIT DMG, +0.2% EVA, +0.5% GOLD per point"
                  />
                </div>
              </div>

              {/* Combat Stats (Derived) */}
              <div className="combat-stats-panel">
                <h4>Combat Stats</h4>
                <div className="combat-stats-grid">
                  <StatChip
                    type="combat"
                    icon="❤️"
                    label="HP"
                    base={baseStats.health}
                    bonus={equipStats.health}
                    buffBonus={buffStats.health}
                    potentialBonus={potentialStats.health}
                    shrineBonus={bonusStats.maxHealth || 0}
                    total={stats.health}
                    tooltip="Health Points - Your maximum HP in combat.&#10;Formula: 100 + (STR × 5) + (INT × 2) + Equipment"
                    isHighlighted={highlightedStats.has('health')}
                  />
                  <StatChip
                    type="combat"
                    icon="⚔️"
                    label="ATK"
                    base={baseStats.attack}
                    bonus={equipStats.attack}
                    buffBonus={buffStats.attack}
                    potentialBonus={potentialStats.attack}
                    total={stats.attack}
                    tooltip="Attack Power - Determines damage dealt in combat.&#10;Formula: 10 + (STR × 1.5) + (DEX × 0.5) + Equipment"
                    isHighlighted={highlightedStats.has('attack')}
                  />
                  <StatChip
                    type="combat"
                    icon="🛡️"
                    label="DEF"
                    base={baseStats.defense}
                    bonus={equipStats.defense}
                    buffBonus={buffStats.defense}
                    potentialBonus={potentialStats.defense}
                    total={stats.defense}
                    tooltip="Defense - Reduces incoming damage in combat.&#10;Formula: 5 + (STR × 0.5) + (INT × 0.8) + Equipment"
                    isHighlighted={highlightedStats.has('defense')}
                  />
                  <StatChip
                    type="combat"
                    icon="🎯"
                    label="ACC"
                    base={baseStats.accuracy * 100}
                    bonus={equipStats.accuracy * 100}
                    buffBonus={buffStats.accuracy * 100}
                    total={stats.accuracy * 100}
                    isPercentage={true}
                    decimals={0}
                    tooltip="Accuracy - Chance to hit enemies in combat.&#10;Formula: 80% + (DEX × 0.5%) + (INT × 0.3%) + Equipment"
                    isHighlighted={highlightedStats.has('accuracy')}
                  />
                  <StatChip
                    type="combat"
                    icon="🌪️"
                    label="EVA"
                    base={baseStats.evasion * 100}
                    bonus={equipStats.evasion * 100}
                    buffBonus={buffStats.evasion * 100}
                    total={stats.evasion * 100}
                    isPercentage={true}
                    decimals={0}
                    tooltip="Evasion - Chance to dodge enemy attacks.&#10;Formula: 5% + (DEX × 0.3%) + (LUK × 0.2%) + Equipment"
                    isHighlighted={highlightedStats.has('evasion')}
                  />
                  <StatChip
                    type="combat"
                    icon="💥"
                    label="CRIT"
                    base={baseStats.critChance * 100}
                    bonus={equipStats.critChance * 100}
                    buffBonus={buffStats.critChance * 100}
                    potentialBonus={potentialStats.critChance * 100}
                    shrineBonus={(bonusStats.critChance || 0) * 100}
                    total={stats.critChance * 100}
                    isPercentage={true}
                    decimals={1}
                    tooltip="Critical Hit Chance - Chance to deal critical damage.&#10;Formula: 5% + (DEX × 0.3%) + (LUK × 0.4%) + Equipment"
                    isHighlighted={highlightedStats.has('critChance')}
                  />
                  <StatChip
                    type="combat"
                    icon="⚡"
                    label="C.DMG"
                    base={baseStats.critDamage * 100}
                    bonus={equipStats.critDamage * 100}
                    buffBonus={buffStats.critDamage * 100}
                    potentialBonus={potentialStats.critDamage * 100}
                    total={stats.critDamage * 100}
                    isPercentage={true}
                    decimals={0}
                    tooltip="Critical Damage - Damage multiplier on critical hits.&#10;Formula: 150% + (LUK × 1%) + Equipment"
                    isHighlighted={highlightedStats.has('critDamage')}
                  />
                  <StatChip
                    type="combat"
                    icon="⚡"
                    label="SPD"
                    base={baseStats.attackSpeed}
                    bonus={equipStats.attackSpeed}
                    buffBonus={buffStats.attackSpeed}
                    total={stats.attackSpeed}
                    isMultiplier={true}
                    decimals={2}
                    tooltip="Attack Speed - Multiplier for attack frequency in combat.&#10;Formula: 1.0x + (DEX × 0.01x) + Equipment"
                    isHighlighted={highlightedStats.has('attackSpeed')}
                  />
                  <StatChip
                    type="combat"
                    icon="💰"
                    label="GOLD"
                    base={baseStats.goldFind * 100}
                    bonus={equipStats.goldFind * 100}
                    buffBonus={buffStats.goldFind * 100}
                    shrineBonus={(bonusStats.goldFind || 0) * 100}
                    total={stats.goldFind * 100}
                    isPercentage={true}
                    decimals={1}
                    tooltip="Gold Find - Bonus gold earned from all sources.&#10;Formula: (LUK × 0.5%) + (INT × 0.3%) + Equipment"
                    isHighlighted={highlightedStats.has('goldFind')}
                  />
                  <StatChip
                    type="combat"
                    icon="⭐"
                    label="EXP"
                    base={baseStats.experienceGain * 100}
                    bonus={equipStats.experienceGain * 100}
                    buffBonus={buffStats.experienceGain * 100}
                    shrineBonus={(bonusStats.xpGain || 0) * 100}
                    total={stats.experienceGain * 100}
                    isPercentage={true}
                    decimals={0}
                    tooltip="Experience Gain - Bonus XP earned from combat.&#10;Formula: 100% + (INT × 0.4%) + Equipment"
                    isHighlighted={highlightedStats.has('xpGain')}
                  />
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* Right: Inventory (5x10 grid with tabs) */}
        <div className="inventory-section-grid">
          <div className="inventory-header">
            <h3>Inventory</h3>
            <span className="inventory-count">{inventory.length}/{playerCharacter.maxInventorySize}</span>
          </div>

          {/* Inventory Tabs */}
          <div className="inventory-tabs">
            <button
              className={`inv-tab ${activeTab === 'equipment' ? 'active' : ''}`}
              onClick={() => setActiveTab('equipment')}
              title="Equipment"
            >
              ⚔️
            </button>
            <button
              className={`inv-tab ${activeTab === 'consumables' ? 'active' : ''}`}
              onClick={() => setActiveTab('consumables')}
              title="Consumables"
            >
              🧪
            </button>
            <button
              className={`inv-tab ${activeTab === 'misc' ? 'active' : ''}`}
              onClick={() => setActiveTab('misc')}
              title="Misc"
            >
              📦
            </button>
            <button
              className={`inv-tab ${activeTab === 'backpack' ? 'active' : ''}`}
              onClick={() => setActiveTab('backpack')}
              title="Backpack"
            >
              🎒
            </button>
          </div>

          {/* Inventory Controls - Right aligned */}
          <div className="inventory-controls">
            <div className="inventory-controls-right">
              <button
                className="inventory-sort-btn"
                onClick={autoSortInventory}
                title="Sort by type & rarity, consolidate stacks"
              >
                📦 Auto Sort
              </button>

              {/* Delete Mode Toggle */}
              <button
                className={`delete-mode-btn ${deleteMode ? 'active' : ''}`}
                onClick={() => {
                  if (deleteMode) {
                    // Turning off delete mode - clear selections
                    setSelectedItems(new Set());
                  }
                  setDeleteMode(!deleteMode);
                }}
                title="Toggle delete mode - Click items to select for deletion"
              >
                {deleteMode ? '✓ Select' : '🗑️ Delete'}
              </button>

              {selectedItems.size > 0 && (
                <button
                  className="inventory-delete-btn"
                  onClick={requestDeleteItems}
                  title={`Delete ${selectedItems.size} item(s)`}
                >
                  🗑️ ({selectedItems.size})
                </button>
              )}

              {/* Trash Zone - Drop items here to delete */}
              <div
                className={`trash-zone ${draggedItem ? 'trash-zone-active' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropOnTrash}
                title="Drag items here to delete"
              >
                🗑️
              </div>
            </div>
          </div>

          {/* 5x10 Grid (50 slots) - Filtered by active tab */}
          <div className="inventory-grid-5x10" key={`inv-tab-${activeTab}`}>
            {useMemo(() => {
              // Pre-compute item positions for O(1) lookup
              // Items with gridPosition stay in their assigned slots
              // Items without gridPosition (virtual items, old items) get placed in empty slots
              const activeItems = categories[activeTab];
              const itemsByPosition = new Map();
              const itemsWithoutPosition = [];

              activeItems.forEach(item => {
                if (item.gridPosition !== undefined) {
                  itemsByPosition.set(item.gridPosition, item);
                } else {
                  itemsWithoutPosition.push(item);
                }
              });

              // Assign positions to items without gridPosition (fill empty slots)
              let nextEmptySlot = 0;
              itemsWithoutPosition.forEach(item => {
                while (itemsByPosition.has(nextEmptySlot) && nextEmptySlot < 50) {
                  nextEmptySlot++;
                }
                if (nextEmptySlot < 50) {
                  itemsByPosition.set(nextEmptySlot, item);
                  nextEmptySlot++;
                }
              });

              return Array.from({ length: 50 }, (_, displayIndex) => {
                // O(1) lookup - items are placed at their stored or assigned gridPosition
                const item = itemsByPosition.get(displayIndex) || null;
                const instanceId = item?.instanceId;

                return (
                <InventorySlot
                  key={instanceId || `empty-${displayIndex}`}
                  item={item}
                  displayIndex={displayIndex}
                  isSelected={instanceId ? selectedItems.has(instanceId) : false}
                  isDragging={draggedItem?.instanceId === instanceId}
                  isDragOver={dragTargetSlot === displayIndex}
                  isFocused={focusedSlot === displayIndex}
                  deleteMode={deleteMode}
                  contextMenu={contextMenu}
                  equipment={equipment}
                  playerStats={playerCharacter.primaryStats}
                  onContextMenu={(e) => {
                    if (deleteMode) {
                      e.preventDefault();
                      toggleItemSelection(instanceId);
                    } else {
                      handleContextMenu(item, e);
                    }
                  }}
                  onDragOver={(e) => handleDragOverInventorySlot(displayIndex, e)}
                  onDragLeave={handleDragLeaveInventorySlot}
                  onDrop={(e) => handleDropOnInventorySlot(displayIndex, item, e)}
                  onDragStart={(e) => {
                    if (!deleteMode && item) handleDragStart(item, instanceId, displayIndex, e);
                  }}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => {
                    if (!item) return;
                    // Delete mode: Click to select
                    if (deleteMode) {
                      toggleItemSelection(instanceId);
                    }
                    // Ctrl+Click for info
                    else if (e.ctrlKey) {
                      setInfoItem(item);
                    }
                    // Shift+Click for selection
                    else if (e.shiftKey) {
                      toggleItemSelection(instanceId);
                    }
                  }}
                  onDoubleClick={(e) => {
                    if (!item) return;
                    // Double-click to equip/use item
                    if (!deleteMode) {
                      if (item.slot) {
                        // Equipment item - equip it
                        handleEquipFromInventory(item, displayIndex);
                      } else if (item.usable) {
                        // Consumable item - use it
                        handleUseItem(item);
                      }
                    }
                  }}
                />
              );
            });
            }, [categories, activeTab, selectedItems, draggedItem, dragTargetSlot, focusedSlot, deleteMode, contextMenu, equipment, playerCharacter.primaryStats])}
          </div>

          {/* Star Essence Currency Bar */}
          <div className="star-essence-bar">
            <span className="star-essence-icon">✦</span>
            <span className="star-essence-amount">{(playerCharacter?.starEssence || 0).toLocaleString()}</span>
            <span className="star-essence-label">Star Essence</span>
          </div>
        </div>
      </div>

      {/* Item Info Modal */}
      {infoItem && (
        <ItemInfoModal
          item={infoItem}
          onClose={() => setInfoItem(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay" onClick={cancelDelete}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-header">
              <span className="delete-confirm-icon">⚠️</span>
              <h3>Delete Items</h3>
            </div>
            <div className="delete-confirm-body">
              <p>Are you sure you want to delete <strong>{selectedItems.size}</strong> item(s)?</p>
              <p className="delete-confirm-warning">This action cannot be undone.</p>
            </div>
            <div className="delete-confirm-footer">
              <button className="delete-confirm-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="delete-confirm-delete" onClick={confirmDeleteItems}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trash Confirmation Modal */}
      {trashConfirm && (
        <div className="trash-confirm-overlay" onClick={cancelTrashDelete}>
          <div className="trash-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="trash-confirm-header">
              <span className="trash-confirm-icon">🗑️</span>
              <h3>Delete Item</h3>
            </div>
            <div className="trash-confirm-body">
              <div className="trash-item-preview">
                <ItemChip
                  item={trashConfirm.item}
                  size="medium"
                  showTooltip={false}
                />
              </div>
              <p>Delete <strong>{trashConfirm.item.name}</strong>?</p>
              {(trashConfirm.item.quantity || 1) > 1 && (
                <p className="trash-quantity-warning">
                  This will delete all <strong>{trashConfirm.item.quantity}</strong> in the stack.
                </p>
              )}
              <p className="trash-confirm-warning">This action cannot be undone.</p>
            </div>
            <div className="trash-confirm-footer">
              <button className="trash-confirm-cancel" onClick={cancelTrashDelete}>
                Cancel
              </button>
              <button className="trash-confirm-delete" onClick={confirmTrashDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Character Guide Modal - First Time Entry */}
      {showCharacterGuide && (
        <CharacterGuideModal onClose={() => setShowCharacterGuide(false)} />
      )}

      {/* Item Context Menu */}
      {contextMenu && (
        <ItemContextMenu
          item={contextMenu.item}
          position={contextMenu.position}
          isEquipped={contextMenu.isEquipped}
          onEquip={(item) => handleEquipFromInventory(item, null)}
          onUnequip={handleUnequipFromContextMenu}
          onUse={handleUseItem}
          onDelete={handleDeleteFromContextMenu}
          onEnhance={handleEnhanceFromContextMenu}
          onForge={handleForgeFromContextMenu}
          onCleanSlate={handleCleanSlateFromContextMenu}
          hasCleanSlateScroll={hasCleanSlateScroll}
          onClose={handleCloseContextMenu}
        />
      )}

      {/* Star Force Enhancement Modal */}
      {starForceItem && (
        <StarForceModal
          item={starForceItem}
          onClose={() => setStarForceItem(null)}
        />
      )}

      {/* Forging (Scroll) Modal */}
      {forgingItem && (
        <ForgingModal
          item={forgingItem}
          onClose={() => setForgingItem(null)}
        />
      )}

      {/* Chest Open Modal */}
      {chestToOpen && (
        <ChestOpenModal
          chest={chestToOpen}
          onClose={() => setChestToOpen(null)}
          onCollect={handleChestCollect}
        />
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`equip-notification ${notification.type}`}>
          <span className="notification-icon">
            {notification.type === 'error' ? '⚠️' : '✅'}
          </span>
          <span className="notification-message">{notification.message}</span>
          <button
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// Equipment Slot Component
function EquipmentSlot({ slot, label, icon, equipment, onUnequip, onDragOver, onDragLeave, onDrop, dragOverSlot, onContextMenu, showTooltip = true }) {
  const isDraggedOver = dragOverSlot === slot;

  return (
    <div className="eq-slot">
      <div className="eq-slot-label">{label}</div>
      {equipment ? (
        <div
          className="eq-slot-filled"
          data-rarity={equipment.rarity}
          onClick={() => onUnequip(slot)}
          onContextMenu={(e) => onContextMenu && onContextMenu(equipment, e, true)}
          onDragOver={(e) => onDragOver(slot, e)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(slot, e)}
        >
          <ItemChip
            item={equipment}
            size="large"
            showTooltip={showTooltip}
            isEquipped={true}
            hideQuantity={true}
          />
        </div>
      ) : (
        <div
          className={`eq-slot-empty ${isDraggedOver ? 'drag-over' : ''}`}
          onDragOver={(e) => onDragOver(slot, e)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(slot, e)}
        >
          <span className="eq-empty-icon">{icon}</span>
        </div>
      )}
    </div>
  );
}

// Primary Stat Row Component (with +1 button)
function PrimaryStatRow({ statName, label, icon, value, hasPoints, onAllocate, tooltip }) {
  return (
    <div className="primary-stat-row" title={tooltip}>
      <span className="primary-stat-icon">{icon}</span>
      <div className="primary-stat-info">
        <span className="primary-stat-label">{label}</span>
        <span className="primary-stat-name">{statName}</span>
      </div>
      <span className="primary-stat-value">{value}</span>
      {hasPoints && (
        <button className="stat-allocate-btn" onClick={onAllocate} title={`Add 1 point to ${label}`}>
          +
        </button>
      )}
    </div>
  );
}

// Combat Stat Row Component (compact, 2-column grid)
function CombatStatRow({ icon, label, base, bonus, total, isPercentage = false, decimals = 0, isMultiplier = false, tooltip = '' }) {
  // Format the display value based on type
  const formatValue = (val) => {
    if (isPercentage) {
      return `${val.toFixed(decimals)}%`;
    }
    if (isMultiplier) {
      return `${val.toFixed(decimals)}x`;
    }
    return Math.floor(val);
  };

  // Determine bonus color (green for positive, red for negative)
  const bonusColor = bonus > 0 ? '#10b981' : bonus < 0 ? '#ef4444' : '#9ca3af';
  const showBonus = bonus !== 0;

  return (
    <div className="combat-stat-row" title={tooltip}>
      <div className="combat-stat-left">
        <span className="combat-stat-icon">{icon}</span>
        <span className="combat-stat-label">{label}</span>
      </div>
      <div className="combat-stat-value">
        <span className="stat-base">{formatValue(base)}</span>
        {showBonus && (
          <span className="stat-bonus" style={{ color: bonusColor }}>
            {' '}({bonus > 0 ? '+' : ''}{formatValue(bonus)})
          </span>
        )}
        {showBonus && <span className="stat-equals"> = </span>}
        {showBonus && <span className="stat-total">{formatValue(total)}</span>}
      </div>
    </div>
  );
}

// Stat Row Component
function StatRow({ icon, label, value }) {
  return (
    <div className="stat-row">
      <span className="stat-icon">{icon}</span>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

export default PlayerPanel;
